import LabeledChip from '@components/v2/ui/LabeledChip/LabeledChip';
import type { LabeledChipAppearance } from '@components/v2/ui/LabeledChip/labeledChip.styles';
import LangEnum from '../../lang/LangEnum';
import { AvailabilityEnum, CompactAvailability } from '../../modules/products/ProductTypes';
import { useTranslation } from '../../modules/translation/TranslationContext';
import type { CategoryColor } from '../../themes/v2/theme';
import { formatDate } from '../../utils/dates';

type AvailabilityValue = `${AvailabilityEnum}` | `${CompactAvailability}`;

interface ProductAvailabilityChipProps {
  availability: AvailabilityValue;
  label?: string;
  disabled?: boolean;
  hideTypeTag?: boolean;
  showFullTypeLabel?: boolean;
  deliveryWindowStartDate?: string | null;
  deliveryWindowEndDate?: string | null;
  maxWidth?: string | number;
  width?: string | number;
  variant?: LabeledChipAppearance;
  className?: string;
}

const AVAILABILITY_TYPE_LABEL: Partial<Record<AvailabilityValue, LangEnum>> = {
  [CompactAvailability.PRE_ORDER]: LangEnum.GLOBAL_PRE_ORDER_SHORT,
  [AvailabilityEnum.INFINITE_PRE_ORDER]: LangEnum.GLOBAL_PRE_ORDER_SHORT,
  [AvailabilityEnum.MATERIAL_PRE_ORDER]: LangEnum.GLOBAL_MATERIAL_PRE_ORDER_SHORT,
  [AvailabilityEnum.FUTURE_STOCK]: LangEnum.GLOBAL_FUTURE_STOCK_SHORT,
  [AvailabilityEnum.READY_FOR_DELIVERY]: LangEnum.GLOBAL_READY_FOR_DELIVERY_SHORT,
};

const AVAILABILITY_TYPE_FULL_LABEL: Partial<Record<AvailabilityValue, LangEnum>> = {
  [CompactAvailability.PRE_ORDER]: LangEnum.GLOBAL_PRE_ORDER,
  [AvailabilityEnum.INFINITE_PRE_ORDER]: LangEnum.GLOBAL_PRE_ORDER,
  [AvailabilityEnum.MATERIAL_PRE_ORDER]: LangEnum.GLOBAL_MATERIAL_PRE_ORDER,
  [AvailabilityEnum.FUTURE_STOCK]: LangEnum.GLOBAL_FUTURE_STOCK,
  [AvailabilityEnum.READY_FOR_DELIVERY]: LangEnum.GLOBAL_READY_FOR_DELIVERY,
};

const AVAILABILITY_TYPE_COLOR: Partial<Record<AvailabilityValue, CategoryColor>> = {
  [CompactAvailability.PRE_ORDER]: 'blue',
  [AvailabilityEnum.INFINITE_PRE_ORDER]: 'blue',
  [AvailabilityEnum.MATERIAL_PRE_ORDER]: 'blue',
  [AvailabilityEnum.FUTURE_STOCK]: 'blue',
  [AvailabilityEnum.READY_FOR_DELIVERY]: 'orange',
};

const ProductAvailabilityChip = ({
  availability,
  label,
  disabled = false,
  hideTypeTag = false,
  showFullTypeLabel = false,
  deliveryWindowStartDate,
  deliveryWindowEndDate,
  maxWidth,
  width,
  variant = 'plain',
  className,
}: ProductAvailabilityChipProps) => {
  const { translate } = useTranslation();

  const typeLabel = showFullTypeLabel
    ? AVAILABILITY_TYPE_FULL_LABEL[availability]
    : AVAILABILITY_TYPE_LABEL[availability];
  const isReadyForDelivery = availability === AvailabilityEnum.READY_FOR_DELIVERY;

  const getContent = () => {
    if (!label) {
      return undefined;
    }

    return isReadyForDelivery ? translate(LangEnum.GLOBAL_READY_FOR_DELIVERY) : label.toLowerCase();
  };

  const getTitle = (content?: string) => {
    if (!deliveryWindowStartDate || !deliveryWindowEndDate) {
      return isReadyForDelivery ? undefined : content;
    }

    const range = translate(LangEnum.ORDER_PRODUCTS_DELIVERY_WINDOW_RANGE_MESSAGE, {
      startDate: formatDate(deliveryWindowStartDate),
      endDate: formatDate(deliveryWindowEndDate),
    });

    return label ? `${label}: ${range}` : range;
  };

  const content = getContent();
  const badgeLabel = typeLabel && !hideTypeTag ? translate(typeLabel) : undefined;

  if (!content && !badgeLabel) {
    return null;
  }

  return (
    <LabeledChip
      appearance={variant}
      color={AVAILABILITY_TYPE_COLOR[availability]}
      label={badgeLabel}
      content={content}
      title={getTitle(content)}
      disabled={disabled}
      className={className}
      sx={{
        width,
        maxWidth: maxWidth || '100%',
        '&.Mui-disabled': { opacity: 1 },
        ...(variant === 'plain' && {
          color: disabled ? 'text.secondary' : 'text.primary',
          fontWeight: '400',
        }),
      }}
    />
  );
};

export default ProductAvailabilityChip;
